import { X } from "lucide-react";
import { useId, useRef, type ReactNode, type RefObject } from "react";
import { useDialogoMecanica } from "../dialogoMecanica";
import { cn } from "../../lib/utils";

/**
 * A CAMADA CRUA de diálogo — véu + painel com a mecânica da casa, e NADA de
 * desenho por cima.
 *
 * 🔴 Por que ela existe (acabamento, 29/07). O #1480 deu casca canônica às três
 * gavetas do financeiro e sobraram ~20 sobreposições artesanais espalhadas:
 * `<div className="fixed inset-0 … bg-black/50">` escrito de novo, cada vez de um
 * jeito, sem `role="dialog"`, sem `aria-modal`, sem foco preso, sem Esc, sem
 * travar a rolagem do corpo e sem devolver o foco pra quem abriu.
 *
 * A saída óbvia seria empurrar todas pro `Modal`. Só que o `Modal` traz uma
 * ANATOMIA junto — barra de cabeçalho com título de texto puro e X à direita — e
 * boa parte dessas telas não tem essa anatomia: a prova de conformidade é uma
 * folha imprimível, o Modo Fiscalização é tela cheia branca, a foto da medição é
 * um lightbox preto, e vários diálogos têm cabeçalho com ícone e subtítulo. Migrar
 * todas pro `Modal` seria trocar conteúdo, não casca — exatamente o que a regra
 * de ouro proíbe.
 *
 * Então esta camada entrega só o que FALTAVA (a mecânica e a semântica) e deixa o
 * desenho onde ele já estava. Ela é a TERCEIRA casca da mesma função —
 * `useDialogoMecanica` — junto do `Modal` e do `Drawer`. Continua existindo uma
 * verdade só: quem consertar o foco preso conserta nos três.
 *
 * Quando usar cada uma:
 *   • `Modal`  — diálogo centrado cujo cabeçalho JÁ é "título + X" puro.
 *   • `Drawer` — gaveta lateral (nasce colada na direita, altura inteira).
 *   • esta     — o resto: cabeçalho próprio, folha imprimível, tela cheia, lightbox.
 */
export function CamadaDeDialogo({
  open = true,
  onClose,
  title,
  children,
  className,
  painelClassName,
  dismissible = true,
  pararPropagacaoNoPainel = true,
  focoInicialRef,
}: {
  /** Default `true` — a maioria destas telas já renderiza com `{aberto && …}`. */
  open?: boolean;
  onClose: () => void;
  /**
   * Nome acessível (vira `aria-labelledby`, invisível). O leitor de tela anuncia
   * isto ao entrar. O título VISÍVEL continua sendo de quem usa a camada — esta
   * aqui não desenha cabeçalho nenhum.
   */
  title: string;
  children: ReactNode;
  /** Classes do véu/contêiner ALÉM de `fixed inset-0` (alinhamento, z, cor). */
  className?: string;
  /** Classes do painel — o desenho que a tela já tinha, copiado sem tocar. */
  painelClassName?: string;
  /**
   * Clique-fora e Esc fecham. Passe `false` onde HOJE o clique fora não fecha:
   * são os diálogos de formulário, e a decisão do Ruddy (02/06) vale pra eles —
   * ninguém perde o que digitou por um Esc de reflexo. Trocar de casca não pode
   * mudar o uso, nem pra "melhor".
   */
  dismissible?: boolean;
  /**
   * O painel engole o clique pra ele não subir até o véu e fechar. É o certo em
   * quase todos — mas nem todos: a prova de conformidade só engole no papel
   * (`#prova-print`), e o lightbox fecha ao clicar na própria foto. Nesses, `false`
   * mantém o comportamento que a tela já tinha.
   */
  pararPropagacaoNoPainel?: boolean;
  /** Onde o foco pousa ao abrir, quando não é o primeiro focável. */
  focoInicialRef?: RefObject<HTMLElement | null>;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogoMecanica({ open, onClose, dismissible, dialogRef, focoInicialRef });

  if (!open) return null;
  return (
    <div className={cn("fixed inset-0 z-50", className)} onClick={dismissible ? onClose : undefined}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={painelClassName}
        onClick={pararPropagacaoNoPainel ? (e) => e.stopPropagation() : undefined}
      >
        <span id={titleId} className="sr-only">{title}</span>
        {children}
      </div>
    </div>
  );
}

/**
 * O X destes diálogos — alvo de toque de 40px e `aria-label`, iguais aos do
 * `Modal` e da gaveta. Quase todos os X artesanais eram `<X className="h-4 w-4">`
 * dentro de um botão sem padding: ~16 a 28px de alvo, abaixo do mínimo, e boa
 * parte sem rótulo nenhum pro leitor de tela.
 */
export function FecharDialogo({ onClose, className }: { onClose: () => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Fechar"
      className={cn(
        "flex min-h-[40px] min-w-[40px] flex-shrink-0 items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      <X className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}
